import { useMemo, useCallback, useRef, useEffect } from "react";
import { AUTH_SOURCE } from "../../../constants/authSource";
import { useGlobalStore } from "../../../context/UserContext/userContext";
import { computeAge } from "../../../utils/helper";
/**
 * hook for processing templates and replacing placeholders in them
 *
 * @param {Object} studyData - study data
 * @param {string} headerContent - header content
 * @param {string} footerContent - footer content
 * @returns {Object} - processed header, footer and function to process text
 */
const useTemplateProcessor = (studyData, headerContent, footerContent) => {
  const { isWixAuth, authSource } = useGlobalStore.getState();
  const isCreAuth = authSource === AUTH_SOURCE.CRE;
  const prevStudyDataRef = useRef();
  const stableStudyDataRef = useRef(studyData);

  // Only update stableStudyData when studyData actually changes
  useEffect(() => {
    if (!studyData) return;
    if (
      JSON.stringify(studyData) !== JSON.stringify(prevStudyDataRef.current)
    ) {
      stableStudyDataRef.current = studyData;
      prevStudyDataRef.current = JSON.stringify(studyData);
    }
  }, [studyData]);

  // function to replace placeholders in text
  // const { tableCatalogItems } = useTableCatalogItems();

  const replacePlaceholders = useCallback(
    (content) => {
      if (!content || !stableStudyDataRef.current) return content;

      let processedContent = content;

      // Use different catalogMapping for Wix and non-Wix
      const catalogMapping = isWixAuth || isCreAuth
        ? {
            "[firstName]": stableStudyDataRef.current.patientName
              ? stableStudyDataRef.current.patientName?.split(" ")[0]
              : "",
            "[lastName]": stableStudyDataRef.current.patientName
              ? stableStudyDataRef.current.patientName?.split(" ")[1]
              : "",
            "[mrn]": stableStudyDataRef.current.modalityCode || "",
            "[patientId]": stableStudyDataRef.current.patientId || "",
            "[accNo]": stableStudyDataRef.current.accessionNo || "",
            "[age]": stableStudyDataRef.current.dateOfBirth
              ? computeAge(stableStudyDataRef.current.dateOfBirth)
              : "",
            "[gender]": stableStudyDataRef.current.patientSex || "",
            "[dateOfBirth]": stableStudyDataRef.current.dateOfBirth || "",
            "[weight]": stableStudyDataRef.current.weight?.toString() || "",
            "[insuranceName]": stableStudyDataRef.current.insuranceName || "",
            "[insuranceNumber]":
              stableStudyDataRef.current.insuranceNumber || "",
          }
        : {
            // STUDY DETAILS mappings
            "[accessionNo]": stableStudyDataRef.current.accessionNo || "",
            "[clinicName]": stableStudyDataRef.current.clinicName || "",
            "[dateOfBirth]": stableStudyDataRef.current.dateOfBirth || "",
            "[finishDatetime]": stableStudyDataRef.current.finishDatetime || "",
            "[imgCount]": stableStudyDataRef.current.imgCount?.toString() || "",
            "[markForCritical]":
              stableStudyDataRef.current.markForCritical?.toString() || "",
            "[modalityAeTitle]":
              stableStudyDataRef.current.modalityAeTitle || "",
            "[modalityCode]": stableStudyDataRef.current.modalityCode || "",
            "[modalityName]": stableStudyDataRef.current.modalityName || "",
            "[ownerName]": stableStudyDataRef.current.ownerName || "",
            "[patientName]": stableStudyDataRef.current.patientName || "",
            "[patientSex]": stableStudyDataRef.current.patientSex || "",
            "[patientSexNeutered]":
              stableStudyDataRef.current.patientSexNeutered || "",
            "[patientWeight]": stableStudyDataRef.current.patientWeight || "",
            "[patientWeightUom]":
              stableStudyDataRef.current.patientWeightUom || "",
            "[physicianName]": stableStudyDataRef.current.physicianName || "",
            "[priorityId]":
              stableStudyDataRef.current.priorityId?.toString() || "",
            "[reviewerNotes]": stableStudyDataRef.current.reviewerNotes || "",
            "[studyDatetime]": stableStudyDataRef.current.studyDatetime || "",
            "[studyStatus]":
              stableStudyDataRef.current.studyStatus?.toString() || "",
            "[submitDatetime]": stableStudyDataRef.current.submitDatetime || "",

            // STUDY FINAL REPORT mappings
            "[ACC_NUMBER_VALUE]": stableStudyDataRef.current.accessionNo || "",
            "[ADDITIONAL COMMENT(S)]":
              stableStudyDataRef.current.clinicalConcerns || "",
            "[APPROVAL_DATE_VALUE]":
              stableStudyDataRef.current.studyInProgress?.finalizedOn || "",
            "[BREED_VALUE]": stableStudyDataRef.current.breedId || "",
            "[CLINICAL HISTORY]":
              stableStudyDataRef.current.clinicalHistory || "",
            "[COMPARISON(S)]":
              stableStudyDataRef.current.priors?.join(", ") || "",
            "[CONTACT_EMAIL]": "", // No direct mapping available
            "[CONTACT_VALUE]": "", // No direct mapping available
            "[DATE_OF_BIRTH_VALUE]":
              stableStudyDataRef.current.dateOfBirth || "",
            "[DICLAIMER_VALUE]": "", // No direct mapping available
            "[IMPRESSION]": "", // No direct mapping available
            "[MODALITY_VALUE]": stableStudyDataRef.current.modalityName || "",
            "[NO_OF_IMAGES_VALUE]":
              stableStudyDataRef.current.imgCount?.toString() || "",
            "[OBSERVATION(S)]": "", // No direct mapping available
            "[OWNER_VALUE]": stableStudyDataRef.current.ownerName || "",
            "[PATIENT_ID_VALUE]": stableStudyDataRef.current.patientId || "",
            "[PATIENT_VALUE]": stableStudyDataRef.current.patientName || "",
            "[RADIOLOGIST_NAME_VALUE]":
              stableStudyDataRef.current.studyInProgress
                ?.finalRadiologistName || "",
            "[REFERRING_SITE_VALUE]":
              stableStudyDataRef.current.clinicName || "",
            "[REFERRING_VET_VALUE]":
              stableStudyDataRef.current.physicianName || "",
            "[SEX_REPRO_STATUS_VALUE]": `${stableStudyDataRef.current
              .patientSex || ""}${
              stableStudyDataRef.current.patientSexNeutered
                ? `/${stableStudyDataRef.current.patientSexNeutered}`
                : ""
            }`,
            "[SPECIES_VALUE]":
              stableStudyDataRef.current.speciesId?.toString() || "",
            "[STUDY TYPE(S)]":
              stableStudyDataRef.current.studyDescription || "",
            "[STUDY_DATE_TIME_VALUE]":
              stableStudyDataRef.current.studyDatetime || "",
            "[SUPPORT_EMAIL]": "support@radsforvets.com", // Assuming this is static
            "[SUPPORT_PHONE]": "1-888-303-RADS (7237)", // Assuming this is static
            "[TECHNIQUE]": "", // No direct mapping available

            // ADDITIONAL MAPPINGS
            "[With or Without;]": stableStudyDataRef.current.ivContrast || "",
            "[without or with]": stableStudyDataRef.current.ivContrast || "",
            "[Without or With]": stableStudyDataRef.current.ivContrast || "",
            "[REASON_FOR_ORDER]":
              stableStudyDataRef.current.reasonForOrder || "", // No direct mapping available
          };

      // Apply all mappings to the content
      for (const [placeholder, value] of Object.entries(catalogMapping)) {
        processedContent = processedContent.split(placeholder).join(value);
      }

      // // Additionally process any catalog items that might have been missed
      // tableCatalogItems.forEach(item => {
      //   const code = item.code || `[${item.name}]`;
      //   if (!catalogMapping[code] && processedContent.includes(code)) {
      //     // If we don't have a mapping but the code exists in content, replace with empty string
      //     processedContent = processedContent.split(code).join('');
      //   }
      // });

      return processedContent;
    },
    [isWixAuth, isCreAuth]
  );

  // processed header and footer
  const processedHeader = useMemo(() => {
    return replacePlaceholders(headerContent || "");
  }, [headerContent, replacePlaceholders]);

  const processedFooter = useMemo(() => {
    return replacePlaceholders(footerContent || "");
  }, [footerContent, replacePlaceholders]);

  return {
    processedHeader,
    processedFooter,
    processText: replacePlaceholders,
  };
};

export default useTemplateProcessor;
